import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { loginUser } from '../../../api/apiServices';
import { useAuth } from '../../../context/useAuth';

export default function LoginPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await loginUser(formData.email, formData.password);

      if (!res?.token) {
        setError(res?.message || 'Invalid email or password.');
        return;
      }

      // Login to context
      login(res.token, res.user);

      // Role-based navigation
      const userRole = res.user?.role?.toLowerCase();

      if (userRole === 'admin' || userRole === 'administrator') {
        navigate('/admin', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      console.error('Login error:', err);

      const errorMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Invalid credentials. Please check your email and password.';

      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Sidebar */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=student%20studying%20online%20learning%20laptop%20modern%20bright%20room%20books%20warm%20lighting%20cozy%20productive%20atmosphere&width=800&height=900&seq=login1&orientation=portrait"
          alt="Student learning"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-teal-900/60 flex flex-col justify-end p-12">
          <img
            src="https://public.readdy.ai/ai/img_res/a37d52a8-201a-4b92-9b36-08215345d1df.png"
            alt="LearnFlow"
            className="h-10 w-auto mb-8 brightness-200"
          />
          <h2 className="text-3xl font-bold text-white mb-3">Welcome back!</h2>
          <p className="text-teal-200 text-lg leading-relaxed max-w-md">
            Continue your learning journey and track your progress across all your courses.
          </p>

          <div className="flex gap-10 mt-10">
            {[
              { number: '50K+', label: 'Students' },
              { number: '4.9★', label: 'Rating' },
              { number: '200+', label: 'Courses' },
            ].map((item) => (
              <div key={item.label} className="text-center">
                <p className="text-3xl font-bold text-white">{item.number}</p>
                <p className="text-teal-300 text-sm mt-1">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8">
            <img
              src="https://public.readdy.ai/ai/img_res/a37d52a8-201a-4b92-9b36-08215345d1df.png"
              alt="LearnFlow"
              className="h-9 w-auto"
            />
          </div>

          <h1 className="text-2xl font-bold text-slate-900 mb-1">Sign in to LearnFlow</h1>
          <p className="text-slate-500 mb-8">
            Don&apos;t have an account?{' '}
            <Link to="/signup" className="text-teal-600 font-semibold hover:text-teal-700">
              Sign up free
            </Link>
          </p>

          {error && (
            <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl mb-6 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Email address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="you@example.com"
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500 bg-white"
              />
            </div>

            {/* Password Field with Toggle */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500 bg-white pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white font-semibold rounded-xl transition-colors disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-8 text-center">
            <Link to="/" className="text-sm text-slate-400 hover:text-slate-600">
              ← Back to home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}